/** WCAG AA minimum contrast ratio for normal text. */
const minTextContrast = 4.5;
function parseColor(color) {
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1].length === 3 ? hex[1].split('').map(char => char + char).join('') : hex[1];
    return [0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16));
  }
  const rgb = color.trim().match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}
function getLuminance([red, green, blue]) {
  const [r, g, b] = [red, green, blue].map(channel => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function getContrastWithWhite(color) {
  const rgb = parseColor(color);
  if (!rgb) return null;
  return 1.05 / (getLuminance(rgb) + 0.05);
}
function getPrimaryTextColor(primaryColor, primaryDarkColor) {
  const contrast = getContrastWithWhite(primaryColor);
  if (!primaryDarkColor || contrast === null || contrast >= minTextContrast) return primaryColor;
  return primaryDarkColor;
}
/** tarteaucitron ships its own stylesheet, with many `!important` rules and a layout partly driven
 * by inline styles set from JS (panel height and position): the selectors below are prefixed with
 * enough ids to win over it, and use `!important` wherever they compete with an inline style or
 * an `!important` rule. */
export function getConsentThemeCss({
  bannerTitle,
  fontFamily,
  primaryColor,
  primaryContrastText = '#fff',
  primaryDarkColor
}) {
  const root = 'html body #tarteaucitronRoot';
  const panel = `${root} #tarteaucitron`;
  const services = `${panel} #tarteaucitronServices`;
  const mainLine = `${services} #tarteaucitronMainLineOffset`;
  const categories = `${services} .tarteaucitronBorder`;
  const serviceLine = `${categories} ul ul li.tarteaucitronLine`;
  const mandatoryLine = `${categories} ul#tarteaucitronServices_mandatory li.tarteaucitronLine`;
  return `
    ${root} {
      --gv-consent-primary: ${primaryColor};
      --gv-consent-primary-contrast: ${primaryContrastText};
      --gv-consent-primary-text: ${getPrimaryTextColor(primaryColor, primaryDarkColor)};
      --gv-consent-text: #283859;
      --gv-consent-text-secondary: #5d687e;
      --gv-consent-border: #dde0e7;
      --gv-consent-surface: #f4f6fa;
    }
    ${root},
    ${root} * {
      font-family: ${fontFamily};
      color: var(--gv-consent-text);
    }
    ${root} .tarteaucitronCheck,
    ${root} .tarteaucitronCross {
      display: none !important;
    }
    ${root} button:focus-visible,
    ${root} a:focus-visible {
      outline: 2px solid var(--gv-consent-primary-text);
      outline-offset: 2px;
    }

    /* Banner, as a floating card. tarteaucitron shows and hides it through an inline display style:
       the flex layout below only applies while it is shown, so that hiding it keeps working. */
    ${root} #tarteaucitronAlertBig {
      box-sizing: border-box !important;
      left: auto !important;
      right: 24px !important;
      bottom: 24px !important;
      width: 420px !important;
      max-width: calc(100vw - 48px) !important;
      margin: 0 !important;
      padding: 20px !important;
      background: #fff !important;
      border: 1px solid var(--gv-consent-border) !important;
      border-radius: 16px;
      box-shadow: 0 12px 40px rgba(23, 32, 51, 0.18) !important;
      font-size: 0 !important;
      text-align: left !important;
    }
    ${root} #tarteaucitronAlertBig[style*='display: block'] {
      display: flex !important;
      flex-wrap: wrap;
      gap: 8px;
    }
    @media screen and (max-width: 767px) {
      ${root} #tarteaucitronAlertBig {
        left: 12px !important;
        right: 12px !important;
        bottom: 12px !important;
        width: auto !important;
        max-width: none !important;
      }
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronDisclaimerAlert {
      order: 1;
      display: block;
      flex-basis: 100%;
      margin: 0 !important;
      padding: 0 !important;
      color: var(--gv-consent-text-secondary);
      font-size: 14px !important;
      line-height: 1.5;
      text-align: left !important;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronDisclaimerAlert::before {
      content: ${JSON.stringify(bannerTitle)};
      display: block;
      margin-bottom: 4px;
      color: var(--gv-consent-text);
      font-size: 16px;
      font-weight: 700;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronPrivacyUrl {
      order: 2;
      display: block !important;
      flex-basis: 100%;
      margin: -4px 0 8px !important;
      padding: 0 !important;
      background: transparent !important;
      color: var(--gv-consent-primary-text);
      font-size: 13px !important;
      font-weight: 600;
      line-height: 1.5;
      text-align: left;
      text-decoration: underline;
      cursor: pointer;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2,
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2,
    ${root} #tarteaucitronAlertBig #tarteaucitronCloseAlert {
      box-sizing: border-box;
      min-height: 40px;
      margin: 0 !important;
      padding: 8px 16px !important;
      border-radius: 8px;
      font-size: 15px !important;
      font-weight: 600;
      line-height: 22px !important;
      text-align: center;
      cursor: pointer;
      transition: background-color 0.15s, box-shadow 0.15s;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2,
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2 {
      flex: 1 1 0;
      background: var(--gv-consent-primary);
      border: 1px solid var(--gv-consent-primary);
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2 {
      order: 3;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2 {
      order: 4;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2,
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2 *,
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2,
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2 * {
      color: var(--gv-consent-primary-contrast);
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronPersonalize2:hover,
    ${root} #tarteaucitronAlertBig #tarteaucitronAllDenied2:hover {
      box-shadow: 0 2px 8px rgba(23, 32, 51, 0.2);
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronCloseAlert {
      order: 5;
      flex-basis: 100%;
      min-height: 36px;
      padding: 6px 16px !important;
      background: transparent;
      border: 0;
      color: var(--gv-consent-primary-text);
      font-size: 14px !important;
    }
    ${root} #tarteaucitronAlertBig #tarteaucitronCloseAlert:hover {
      background: var(--gv-consent-surface);
    }

    /* Panel */
    ${root} #tarteaucitronBack {
      background: rgba(23, 32, 51, 0.48) !important;
      opacity: 1 !important;
    }
    ${root} div#tarteaucitron {
      overflow: hidden;
      background: #fff;
      border: 0 !important;
      box-shadow: 0 24px 64px rgba(23, 32, 51, 0.24);
    }
    ${services} {
      box-sizing: border-box;
      margin-top: 0 !important;
      overflow-x: hidden !important;
      overflow-y: auto !important;
    }
    @media screen and (min-width: 768px) {
      ${root} div#tarteaucitron {
        top: 50% !important;
        left: 50% !important;
        right: auto !important;
        width: 640px !important;
        max-width: calc(100vw - 32px) !important;
        max-height: calc(100vh - 48px) !important;
        margin: 0 !important;
        border-radius: 16px;
        transform: translate(-50%, -50%);
      }
      ${services} {
        height: auto !important;
        max-height: calc(100vh - 48px) !important;
      }
    }
    @media screen and (max-width: 767px) {
      ${services} {
        height: 100% !important;
      }
    }
    ${panel} #tarteaucitronClosePanel {
      position: absolute !important;
      top: 16px !important;
      right: 16px !important;
      left: auto !important;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px !important;
      height: 36px;
      padding: 0 !important;
      background: var(--gv-consent-surface) !important;
      border: 0;
      border-radius: 50% !important;
      color: transparent;
      font-size: 0 !important;
      cursor: pointer;
    }
    ${panel} #tarteaucitronClosePanel:hover {
      background: var(--gv-consent-border) !important;
    }
    ${panel} #tarteaucitronClosePanel::before {
      content: '\\2715';
      color: var(--gv-consent-text);
      font-size: 16px;
      line-height: 1;
    }

    /* Panel header and global choice */
    ${mainLine} {
      display: grid !important;
      grid-template-columns: 1fr auto;
      align-items: center;
      column-gap: 16px;
      margin: 0 !important;
      padding: 24px 24px 20px !important;
      background: #fff !important;
      border: 0 !important;
    }
    ${mainLine} .tarteaucitronH1 {
      grid-column: 1 / -1;
      display: block;
      margin: 4px 56px 8px 0 !important;
      color: var(--gv-consent-text);
      font-size: 20px !important;
      font-weight: 700;
      line-height: 1.3;
      text-align: left !important;
    }
    ${mainLine} #tarteaucitronInfo {
      grid-column: 1 / -1;
      max-width: none !important;
      margin: 0 0 20px !important;
      padding: 0 0 20px !important;
      border: 0 !important;
      border-bottom: 1px solid var(--gv-consent-border) !important;
      text-align: left !important;
    }
    ${mainLine} #tarteaucitronInfo p {
      color: var(--gv-consent-text-secondary);
      font-size: 14px;
      line-height: 1.5;
    }
    ${mainLine} #tarteaucitronInfo br {
      display: none;
    }
    ${mainLine} #tarteaucitronPrivacyUrlDialog {
      display: block;
      margin: 8px 0 0 !important;
      padding: 0 !important;
      background: none !important;
      border: 0;
      color: var(--gv-consent-primary-text) !important;
      font-size: 14px !important;
      font-weight: 600;
      text-decoration: underline;
      cursor: pointer;
    }
    ${mainLine} .tarteaucitronName {
      display: block !important;
      float: none !important;
      width: auto !important;
      margin: 0 !important;
    }
    ${mainLine} .tarteaucitronH2 {
      margin: 0 !important;
      font-size: 15px !important;
      font-weight: 700;
    }
    @media screen and (max-width: 767px) {
      ${mainLine} {
        grid-template-columns: 1fr;
        row-gap: 12px;
      }
    }

    /* Allow / deny choices, as segmented controls */
    ${services} .tarteaucitronAsk {
      display: inline-flex !important;
      gap: 2px;
      float: none !important;
      box-sizing: border-box !important;
      width: auto !important;
      margin: 0 !important;
      padding: 3px !important;
      background: #fff;
      border: 1px solid var(--gv-consent-border);
      border-radius: 10px;
      text-align: left !important;
    }
    ${services} .tarteaucitronAsk button.tarteaucitronAllow,
    ${services} .tarteaucitronAsk button.tarteaucitronDeny {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      width: auto !important;
      margin: 0 !important;
      padding: 6px 14px !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 7px !important;
      color: var(--gv-consent-text-secondary) !important;
      font-size: 14px !important;
      font-weight: 600;
      line-height: 20px;
      white-space: nowrap;
      opacity: 1 !important;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
    }
    ${services} .tarteaucitronAsk button.tarteaucitronAllow:hover,
    ${services} .tarteaucitronAsk button.tarteaucitronDeny:hover {
      background: var(--gv-consent-surface) !important;
      color: var(--gv-consent-text) !important;
    }
    ${services} #tarteaucitronAllAllowed.tarteaucitronIsSelected,
    ${services} #tarteaucitronAllDenied.tarteaucitronIsSelected,
    ${services} .tarteaucitronIsAllowed .tarteaucitronAsk button.tarteaucitronAllow,
    ${services} .tarteaucitronIsDenied .tarteaucitronAsk button.tarteaucitronDeny,
    ${services} #tarteaucitronAllAllowed.tarteaucitronIsSelected:hover,
    ${services} #tarteaucitronAllDenied.tarteaucitronIsSelected:hover,
    ${services} .tarteaucitronIsAllowed .tarteaucitronAsk button.tarteaucitronAllow:hover,
    ${services} .tarteaucitronIsDenied .tarteaucitronAsk button.tarteaucitronDeny:hover {
      background: var(--gv-consent-primary) !important;
      color: var(--gv-consent-primary-contrast) !important;
    }
    @media screen and (max-width: 767px) {
      ${services} .tarteaucitronAsk {
        display: flex !important;
        width: 100% !important;
      }
      ${services} .tarteaucitronAsk button.tarteaucitronAllow,
      ${services} .tarteaucitronAsk button.tarteaucitronDeny {
        flex: 1;
      }
    }

    /* Categories */
    ${categories} {
      padding: 0 24px 8px;
      background: #fff;
      border: 0 !important;
    }
    ${categories} > ul > li {
      border-top: 1px solid var(--gv-consent-border);
    }
    ${categories} > ul > li:first-child {
      border-top: 0;
    }
    ${categories} .tarteaucitronTitle {
      display: block;
      margin: 0 !important;
      padding: 0 !important;
      background: none !important;
    }
    ${categories} .tarteaucitronTitle button {
      position: static !important;
      display: flex !important;
      align-items: center;
      justify-content: space-between;
      width: 100% !important;
      margin: 0 !important;
      padding: 16px 0 !important;
      background: none !important;
      border: 0;
      color: var(--gv-consent-text) !important;
      font-size: 15px !important;
      font-weight: 700 !important;
      text-align: left;
      cursor: pointer;
    }
    ${categories} #tarteaucitronServicesTitle_mandatory .tarteaucitronTitle button {
      padding-bottom: 4px !important;
      cursor: default;
    }
    ${categories} .tarteaucitronPlus {
      display: none !important;
    }
    ${categories} .tarteaucitronTitle button.catToggleBtn {
      padding-bottom: 4px !important;
      cursor: default;
      pointer-events: none;
    }
    ${categories} .tarteaucitronDetails {
      display: block !important;
      position: static !important;
      width: auto !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 0 12px !important;
      background: none !important;
      color: var(--gv-consent-text-secondary) !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.5;
    }

    /* Services */
    ${serviceLine} {
      display: flex !important;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin: 0 0 12px !important;
      padding: 12px 16px !important;
      background: var(--gv-consent-surface) !important;
      border: 0 !important;
      border-radius: 12px;
    }
    ${serviceLine} .tarteaucitronName {
      flex: 1 1 200px;
      float: none !important;
      width: auto !important;
      margin: 0 !important;
      text-align: left;
    }
    ${serviceLine} .tarteaucitronName span {
      width: auto !important;
    }
    ${serviceLine} .tarteaucitronH3 {
      display: block !important;
      margin: 0 0 2px !important;
      color: var(--gv-consent-text);
      font-size: 15px !important;
      font-weight: 700 !important;
    }
    ${serviceLine} .tarteaucitronStatusInfo,
    ${serviceLine} .tarteaucitronName > .tarteaucitronReadmoreSeparator,
    ${serviceLine} a.tarteaucitronReadmoreOfficial {
      display: none !important;
    }
    ${serviceLine} a.tarteaucitronReadmoreInfo {
      color: var(--gv-consent-primary-text) !important;
      font-size: 13px !important;
      font-weight: 600;
      text-decoration: none;
    }
    ${serviceLine} a.tarteaucitronReadmoreInfo:hover {
      text-decoration: underline;
    }
    ${mandatoryLine} {
      display: block !important;
      margin: 0 !important;
      padding: 0 0 16px !important;
      background: none !important;
    }
    ${mandatoryLine} br,
    ${mandatoryLine} .tarteaucitronListCookies {
      display: none !important;
    }
    ${mandatoryLine} .tarteaucitronH3 {
      color: var(--gv-consent-text-secondary);
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.5;
    }

    /* Save */
    ${services} #tarteaucitronSave {
      position: sticky;
      bottom: 0;
      z-index: 1;
      padding: 16px 24px !important;
      background: #fff !important;
      border-top: 1px solid var(--gv-consent-border);
      text-align: right !important;
    }
    ${services} #tarteaucitronSave button#tarteaucitronSaveButton {
      margin: 0 !important;
      padding: 10px 24px !important;
      background: var(--gv-consent-primary) !important;
      border: 0;
      border-radius: 8px !important;
      color: var(--gv-consent-primary-contrast) !important;
      font-size: 15px !important;
      font-weight: 600;
      line-height: 20px;
      cursor: pointer;
    }
    ${services} #tarteaucitronSave button#tarteaucitronSaveButton:hover {
      box-shadow: 0 2px 8px rgba(23, 32, 51, 0.2);
    }
  `;
}